import { ColorPalette, OrganizationBranding } from "./branding.types";
import { normalizeOrgName } from "./org-normalize.util";

/** ZosmaMedia Primary (peach) - base 300 */
const ZOSMA_PRIMARY: ColorPalette = {
    50: "#F5EAE8",
    100: "#F3CFC1",
    200: "#EDB19A",
    300: "#E79473",
    400: "#E47F55",
    500: "#E56A36",
    600: "#DA6432",
    700: "#CD5D2E",
    800: "#BF562A",
    900: "#A74922",
};

/** ZosmaMedia Secondary (navy) - base 900 */
const ZOSMA_SECONDARY: ColorPalette = {
    50: "#E7EAF0",
    100: "#C3C9DB",
    200: "#9DA7C2",
    300: "#7885A9",
    400: "#5C6B98",
    500: "#415289",
    600: "#3B4B80",
    700: "#324175",
    800: "#2C3868",
    900: "#212850",
};

/** Geo-Avante Primary (red) - base 500 */
const GEO_AVANTE_PRIMARY: ColorPalette = {
    50: "#FFD3C9",
    100: "#FFAEA3",
    200: "#FF8B81",
    300: "#FF6861",
    400: "#F14544",
    500: "#D8242C",
    600: "#B6000D",
    700: "#950000",
    800: "#730000",
    900: "#510000",
};

/** Geo-Avante Secondary (burgundy) - base 700 */
const GEO_AVANTE_SECONDARY: ColorPalette = {
    50: "#FBE9EC",
    100: "#F5CCD0",
    200: "#E7A6AE",
    300: "#D47D87",
    400: "#BF4A58",
    500: "#A33340",
    600: "#902C38",
    700: "#7A2832",
    800: "#5E1D24",
    900: "#3F1218",
};

/** GeoHex Primary (purple) - base 500 */
const GEOHEX_PRIMARY: ColorPalette = {
    50: "#E2EBFF",
    100: "#C4CCFF",
    200: "#A8AEFF",
    300: "#8F90FF",
    400: "#7773FF",
    500: "#6157F2",
    600: "#4D3BD4",
    700: "#3B20B2",
    800: "#2A008E",
    900: "#1B0068",
};

/** GeoHex Secondary (blue) - base 700 */
const GEOHEX_SECONDARY: ColorPalette = {
    50: "#E3F2FF",
    100: "#C4D5FF",
    200: "#A8B9FF",
    300: "#8D9EFA",
    400: "#7483E2",
    500: "#5C6AC8",
    600: "#4752AC",
    700: "#333B8E",
    800: "#22256E",
    900: "#12114E",
};

/** Irismedia Primary (green) - base 500 */
const IRISMEDIA_PRIMARY: ColorPalette = {
    50: "#F7FBF5",
    100: "#EEF7EB",
    200: "#DAEDD2",
    300: "#BEE0B1",
    400: "#9CCF89",
    500: "#55AD33",
    600: "#418726",
    700: "#255115",
    800: "#0B1906",
    900: "#000000",
};

/** Irismedia Secondary (dark green) - base 500 */
const IRISMEDIA_SECONDARY: ColorPalette = {
    50: "#F4F7F4",
    100: "#EAF0E8",
    200: "#D0DDCC",
    300: "#AEC4A8",
    400: "#83A67A",
    500: "#296519",
    600: "#183D0E",
    700: "#010301",
    800: "#000000",
    900: "#000000",
};

/** Global Primary (blue) - base 500 */
const GLOBAL_PRIMARY: ColorPalette = {
    50: "#F4F7FB",
    100: "#E8EFF6",
    200: "#CCDBEB",
    300: "#A8C0DD",
    400: "#7A9FCB",
    500: "#195AA6",
    600: "#11427B",
    700: "#07203D",
    800: "#000000",
    900: "#000000",
};

/** Global Secondary (cyan) - base 500 */
const GLOBAL_SECONDARY: ColorPalette = {
    50: "#F7FCFE",
    100: "#EFF9FD",
    200: "#DBF2FA",
    300: "#C1E9F6",
    400: "#A0DDF2",
    500: "#5BC4E8",
    600: "#2CB5E4",
    700: "#148CB5",
    800: "#0B5B76",
    900: "#042936",
};

/** Havas Primary (red) - base 500 */
const HAVAS_PRIMARY: ColorPalette = {
    50: "#FDE5E5",
    100: "#FBC2C2",
    200: "#F79A9A",
    300: "#F26B6B",
    400: "#EE3838",
    500: "#E60000",
    600: "#C40000",
    700: "#A30000",
    800: "#7A0000",
    900: "#520000",
};

/** Havas Secondary (black/grey) - base 900 */
const HAVAS_SECONDARY: ColorPalette = {
    50: "#F5F5F5",
    100: "#E0E0E0",
    200: "#BDBDBD",
    300: "#9E9E9E",
    400: "#757575",
    500: "#616161",
    600: "#424242",
    700: "#303030",
    800: "#1A1A1A",
    900: "#000000",
};

/** ClearChannel Primary (navy) - base 900 */
const CLEAR_CHANNEL_PRIMARY: ColorPalette = {
    50: "#F2F4F8",
    100: "#DCE0E9",
    200: "#BFC5D2",
    300: "#99A1B3",
    400: "#737E96",
    500: "#4D5A7A",
    600: "#36446B",
    700: "#1A2C5A",
    800: "#0D2152",
    900: "#001649",
};

/** ClearChannel Secondary (cyan) - base 500 */
const CLEAR_CHANNEL_SECONDARY: ColorPalette = {
    50: "#E5F5FB",
    100: "#BFE7F7",
    200: "#99D7F1",
    300: "#66BFE7",
    400: "#33ACDF",
    500: "#0099D7",
    600: "#007FB7",
    700: "#006690",
    800: "#004D6C",
    900: "#002D40",
};

/** JCDecaux Primary (navy) - base 900 */
const JCDECAUX_PRIMARY: ColorPalette = {
    50: "#F2F4F7",
    100: "#D9E0E6",
    200: "#B3C2CE",
    300: "#8CA3B5",
    400: "#66849D",
    500: "#406684",
    600: "#265174",
    700: "#144168",
    800: "#083860",
    900: "#00335B",
};

/** JCDecaux Secondary (teal) - base 500 */
const JCDECAUX_SECONDARY: ColorPalette = {
    50: "#F4FCFC",
    100: "#D2F4F4",
    200: "#B1EBEC",
    300: "#84DEE0",
    400: "#59D3D5",
    500: "#22B6BB",
    600: "#1D9B9F",
    700: "#16767A",
    800: "#115B5E",
    900: "#0A3738",
};

/** Mediaplus Primary (coral red) - base 500 */
const MEDIAPLUS_PRIMARY: ColorPalette = {
    50: "#FFE8E9",
    100: "#FFD1D2",
    200: "#FFB4B6",
    300: "#FF9092",
    400: "#FF6D70",
    500: "#FF4B4F",
    600: "#D94043",
    700: "#A63133",
    800: "#802628",
    900: "#4D1718",
};

/** Mediaplus Secondary (black/grey) - base 900 */
const MEDIAPLUS_SECONDARY: ColorPalette = {
    50: '#F5F5F5',
    100: '#E0E0E0',
    200: '#BDBDBD',
    300: '#9E9E9E',
    400: '#757575',
    500: '#616161',
    600: '#424242',
    700: '#303030',
    800: '#1A1A1A',
    900: '#000000',
};

/** We24 Primary (lime green) - base 500 */
const WE24_PRIMARY: ColorPalette = {
    50: "#F4F8E4",
    100: "#E2EFB8",
    200: "#CFE689",
    300: "#BBDC59",
    400: "#ABD235",
    500: "#93C11E",
    600: "#82AC1A",
    700: "#6E9214",
    800: "#587410",
    900: "#41560B",
};

/** We24 Secondary (charcoal) - base 500 */
const WE24_SECONDARY: ColorPalette = {
    50: "#ECECEC",
    100: "#D1D1D1",
    200: "#ADADAD",
    300: "#8A8A8A",
    400: "#5F5F5F",
    500: "#373737",
    600: "#2F2F2F",
    700: "#262626",
    800: "#1C1C1C",
    900: "#111111",
};

/** Expertia Primary (orange) - base 500 */
const EXPERTIA_PRIMARY: ColorPalette = {
    50: "#FDF1E8",
    100: "#FBDDC2",
    200: "#F6BC8C",
    300: "#F19957",
    400: "#ED8838",
    500: "#E97522",
    600: "#D26720",
    700: "#B5571B",
    800: "#984616",
    900: "#7A380F",
};

/** Expertia Secondary (teal) - base 500 */
const EXPERTIA_SECONDARY: ColorPalette = {
    50: "#E6F7F8",
    100: "#BFECEF",
    200: "#91DDE2",
    300: "#5FCBD3",
    400: "#3CB7C0",
    500: "#1EA3B1",
    600: "#1A8C99",
    700: "#157180",
    800: "#105768",
    900: "#0A3E4D",
};

/** LoudIntelligence Primary (blue-indigo) - base 500 */
const LOUD_INTELLIGENCE_PRIMARY: ColorPalette = {
    50: "#ECECFF",
    100: "#D0D0FF",
    200: "#B3B2FF",
    300: "#9695FF",
    400: "#6562FF",
    500: "#3532FF",
    600: "#2E2BE6",
    700: "#2421B3",
    800: "#1B1980",
    900: "#11104D",
};

/** LoudIntelligence Secondary (burnt orange) - base 500 */
const LOUD_INTELLIGENCE_SECONDARY: ColorPalette = {
    50: "#FBE8D9",
    100: "#F4C4A3",
    200: "#ED9F6D",
    300: "#E37937",
    400: "#D75D1B",
    500: "#BB4101",
    600: "#A53901",
    700: "#8E3001",
    800: "#762801",
    900: "#5C1F01",
};
/** Adissey Primary (dark navy) - base 900 */
const ADISSEY_PRIMARY: ColorPalette = {
    50: "#F1F2F5",
    100: "#DCDEE6",
    200: "#BFC2D1",
    300: "#9CA1B8",
    400: "#6E7491",
    500: "#474E70",
    600: "#353C5B",
    700: "#282E4B",
    800: "#1E243F",
    900: "#181F39",
};

/** Adissey Secondary (lime) - base 500 */
const ADISSEY_SECONDARY: ColorPalette = {
    50: "#FAFFEC",
    100: "#F1FFCC",
    200: "#E6FFA8",
    300: "#DBFF85",
    400: "#D3FF71",
    500: "#CEFF67",
    600: "#B2E33F",
    700: "#8FB92E",
    800: "#6B8C22",
    900: "#4A6017",
};

/** Imooh Primary (navy) - base 700 (#1C3B7B is dark, so it sits at 700 instead of 500) */
const IMOOH_PRIMARY: ColorPalette = {
    50: "#EEF2FA",
    100: "#D6DFF2",
    200: "#B0C1E4",
    300: "#829DD0",
    400: "#4D6FB0",
    500: "#2E529A",
    600: "#234587",
    700: "#1C3B7B",
    800: "#172F62",
    900: "#11244B",
};

/** Imooh Secondary / accent (red, matches the logo) - base 500 */
const IMOOH_SECONDARY: ColorPalette = {
    50: "#FDECEC",
    100: "#FAD1D1",
    200: "#F4A6A6",
    300: "#EC7373",
    400: "#E24C4C",
    500: "#D02828",
    600: "#B31F1F",
    700: "#911919",
    800: "#701414",
    900: "#4F0E0E",
};

/** Vocento Primary (navy) - base 500 */
const VOCENTO_PRIMARY: ColorPalette = {
    50: "#FCFCFD",
    100: "#EEEEF1",
    200: "#CED0D8",
    300: "#A8ACBB",
    400: "#777E99",
    500: "#00316C",
    600: "#002C61",
    700: "#002654",
    800: "#001F45",
    900: "#001631",
};

/** Vocento Secondary (black) - base 500 */
const VOCENTO_SECONDARY: ColorPalette = {
    50: "#FCFCFC",
    100: "#EEEEEE",
    200: "#CECECE",
    300: "#A8A8A8",
    400: "#777777",
    500: "#000000",
    600: "#000000",
    700: "#000000",
    800: "#000000",
    900: "#000000",
};

/** Vocento Tertiary (cyan) - base 500 */
const VOCENTO_TERTIARY: ColorPalette = {
    50: "#FCFEFF",
    100: "#EEF8FF",
    200: "#CEEBFF",
    300: "#A8DDFE",
    400: "#77CFFE",
    500: "#03BFFE",
    600: "#03ABE4",
    700: "#0295C6",
    800: "#027AA2",
    900: "#015875",
};

const ZOSMA_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#E79473",
    accentColor: "#212850",
    primaryPalette: ZOSMA_PRIMARY,
    accentPalette: ZOSMA_SECONDARY,
    primaryBaseShade: 300,
    accentBaseShade: 900,
    logoUrl: "assets/images/logo/zosmalogo/Zosma_logotype.png",
    logoDarkUrl: "assets/images/logo/zosmalogo/Zosma_logotype_white.png",
    logoMaxWidth: 180,
    logoHeight: 180,
    logoWidthPercent: 95,
    faviconUrl: "assets/images/logo/zosmalogo/Zosma_isotype.png",
    documentTitle: "Zosma Atlas",
};

const GEO_AVANTE_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#D8242C",
    accentColor: "#7A2832",
    primaryPalette: GEO_AVANTE_PRIMARY,
    accentPalette: GEO_AVANTE_SECONDARY,
    primaryBaseShade: 500,
    accentBaseShade: 700,
    logoUrl: "assets/images/logo/geoavantlogo/Geo-Avante.png",
    logoDarkUrl: "assets/images/logo/geoavantlogo/Geo-Avante-white.png",
    logoMaxWidth: 180,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "Geo-Avante",
    faviconUrl: "assets/images/logo/geoavantlogo/GeoAvante_isotype.png",
    fontFamily:
        '"Montserrat", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif',
};

const GEOHEX_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#6157F2",
    accentColor: "#333B8E",
    primaryPalette: GEOHEX_PRIMARY,
    accentPalette: GEOHEX_SECONDARY,
    primaryBaseShade: 500,
    accentBaseShade: 700,
    logoUrl: "assets/images/logo/geohexlogo/GeoHex_logotype.png",
    logoDarkUrl: "assets/images/logo/geohexlogo/GeoHex_logotype--white.png",
    logoMaxWidth: 180,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "GeoHex",
    faviconUrl: "assets/images/logo/geohexlogo/GeoHex_isotype.png",
};

const IRISMEDIA_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#55AD33",
    accentColor: "#296519",
    primaryPalette: IRISMEDIA_PRIMARY,
    accentPalette: IRISMEDIA_SECONDARY,
    primaryBaseShade: 500,
    accentBaseShade: 500,
    logoUrl: "assets/images/logo/irismedialogo/Irismedia_logotype.png",
    logoDarkUrl: "assets/images/logo/irismedialogo/Irismedia_logotype--white.png",
    logoMaxWidth: 180,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "IrisLens",
    faviconUrl: "assets/images/logo/irismedialogo/Irismedia_isotype.png",
};

const GLOBAL_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#195AA6",
    accentColor: "#5BC4E8",
    primaryPalette: GLOBAL_PRIMARY,
    accentPalette: GLOBAL_SECONDARY,
    primaryBaseShade: 500,
    accentBaseShade: 500,
    logoUrl: "assets/images/logo/globallogo/Global_logotype.png",
    logoDarkUrl: "assets/images/logo/globallogo/Global_logotype--white.png",
    logoMaxWidth: 180,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "Global",
    faviconUrl: "assets/images/logo/globallogo/Global_isotype.png",
};

const HAVAS_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#E60000",
    accentColor: "#000000",
    primaryPalette: HAVAS_PRIMARY,
    accentPalette: HAVAS_SECONDARY,
    primaryBaseShade: 500,
    accentBaseShade: 900,
    logoUrl: "assets/images/logo/proximia/Proximia_logotype.png",
    logoDarkUrl: "assets/images/logo/proximia/Proximia_logotype--white.png",
    logoMaxWidth: 180,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "Do It Yourself",
    faviconUrl: "assets/images/logo/proximia/Proximia_isotype.png",
};

const CLEAR_CHANNEL_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#001649",
    accentColor: "#0099D7",
    primaryPalette: CLEAR_CHANNEL_PRIMARY,
    accentPalette: CLEAR_CHANNEL_SECONDARY,
    primaryBaseShade: 900,
    accentBaseShade: 500,
    logoUrl: "assets/images/logo/clearchannel/ClearChannel_logotype.png",
    logoDarkUrl: "assets/images/logo/clearchannel/ClearChannel_logotype--white.png",
    logoMaxWidth: 180,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "Clear Channel",
    faviconUrl: "assets/images/logo/clearchannel/ClearChannel_isotype.png",
};

const JCDECAUX_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#00335B",
    accentColor: "#22B6BB",
    primaryPalette: JCDECAUX_PRIMARY,
    accentPalette: JCDECAUX_SECONDARY,
    primaryBaseShade: 900,
    accentBaseShade: 500,
    logoUrl: "assets/images/logo/jcdecaux/JCDecaux_logotype.png",
    logoDarkUrl: "assets/images/logo/jcdecaux/JCDecaux_logotype--white.png",
    logoMaxWidth: 180,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "JCDecaux",
    faviconUrl: "assets/images/logo/jcdecaux/JCDecaux_isotype.svg",
};

const MEDIAPLUS_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#FF4B4F",
    accentColor: "#000000",
    primaryPalette: MEDIAPLUS_PRIMARY,
    accentPalette: MEDIAPLUS_SECONDARY,
    primaryBaseShade: 500,
    accentBaseShade: 900,
    logoUrl: "assets/images/logo/mediaplus/Mediaplus_logotype.png",
    logoDarkUrl: "assets/images/logo/mediaplus/Mediaplus_logotype--white.png",
    logoMaxWidth: 180,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "Mediaplus",
    faviconUrl: "assets/images/logo/mediaplus/Mediaplus_logotype.png",
};

const LOUD_INTELLIGENCE_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#3532FF",
    accentColor: "#BB4101",
    primaryPalette: LOUD_INTELLIGENCE_PRIMARY,
    accentPalette: LOUD_INTELLIGENCE_SECONDARY,
    primaryBaseShade: 500,
    accentBaseShade: 500,
    logoUrl: "assets/images/logo/LoudIntelligence/LoudIntelligence_logo_dark.svg",
    logoDarkUrl: "assets/images/logo/LoudIntelligence/LoudIntelligence_logo_white.svg",
    logoMaxWidth: 120,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "Loud Intelligence",
    faviconUrl: "assets/images/logo/LoudIntelligence/LoudIntelligence_isotype.svg",
    /* No loginImageUrl: loudintelligence uses the CSS-rendered animated welcome
       panel (primary background + two secondary-colored spheres) instead of a
       static image. */
};

const EXPERTIA_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#E97522",
    accentColor: "#1EA3B1",
    primaryPalette: EXPERTIA_PRIMARY,
    accentPalette: EXPERTIA_SECONDARY,
    primaryBaseShade: 500,
    accentBaseShade: 500,
    logoUrl: "assets/images/logo/expertia/expertia-logotype.svg",
    logoDarkUrl: "assets/images/logo/expertia/expertia_logotype_white.svg",
    logoMaxWidth: 160,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "Expertia",
    faviconUrl: "assets/images/logo/expertia/expertia_isotype.svg",
    /* No loginImageUrl: expertia uses the CSS-rendered animated welcome panel
       (primary orange background + two teal-colored spheres) instead of a static image. */
};

const WE24_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#93C11E",
    accentColor: "#373737",
    primaryPalette: WE24_PRIMARY,
    accentPalette: WE24_SECONDARY,
    primaryBaseShade: 500,
    accentBaseShade: 500,
    logoUrl: "assets/images/logo/we24/logo-we24-color.svg",
    logoDarkUrl: "assets/images/logo/we24/logo-we24-blanco.svg",
    logoMaxWidth: 80,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "We24",
    faviconUrl: "assets/images/logo/we24/We24_isotype.svg",
    /* No loginImageUrl: we24 uses the CSS-rendered animated welcome panel
       (primary background + two secondary-colored spheres) instead of a static image. */
};

const ADISSEY_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#181F39",
    accentColor: "#CEFF67",
    primaryPalette: ADISSEY_PRIMARY,
    accentPalette: ADISSEY_SECONDARY,
    primaryBaseShade: 900,
    accentBaseShade: 500,
    logoUrl: "assets/images/logo/adissey/radius/adissey_radius_logo.svg",
    logoDarkUrl: "assets/images/logo/adissey/radius/adissey_radius_white_logo.svg",
    logoMaxWidth: 168,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "Adissey",
    faviconUrl: "assets/images/logo/adissey/radius/Radius_isotype.svg",
    /* No loginImageUrl: adissey uses the CSS-rendered animated welcome panel
       (primary navy background + two secondary-colored lime spheres) instead of a static image. */
};

const IMOOH_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#1C3B7B",
    accentColor: "#D02828",
    primaryPalette: IMOOH_PRIMARY,
    accentPalette: IMOOH_SECONDARY,
    primaryBaseShade: 700,
    accentBaseShade: 500,
    logoUrl: "assets/images/logo/Imooh/Imooh_logotype.svg",
    logoDarkUrl: "assets/images/logo/Imooh/Imooh_logotype--white.svg",
    logoMaxWidth: 168,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "Imooh",
    faviconUrl: "assets/images/logo/Imooh/Imooh_isotype.svg",
    fontFamily:
        '"Montserrat", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif',
    /* No loginImageUrl: imooh uses the CSS-rendered animated welcome panel. */
};

/*
 * Localytics shares Havas' palette (primary #E60000 / secondary #000000) and
 * reuses the assets under assets/images/logo/havas/ (HAVAS_*).
 * Naming trap: that `havas/` folder belongs to Localytics. The `havas` org key
 * (HAVAS_BRANDING, documentTitle "Do It Yourself") instead points at
 * assets/images/logo/proximia/ (Proximia_*).
 */
const LOCALYTICS_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#E60000",
    accentColor: "#000000",
    primaryPalette: HAVAS_PRIMARY,
    accentPalette: HAVAS_SECONDARY,
    primaryBaseShade: 500,
    accentBaseShade: 900,
    logoUrl: "assets/images/logo/havas/HAVAS_logotype.svg",
    logoDarkUrl: "assets/images/logo/havas/HAVAS_logotype--white.svg",
    logoMaxWidth: 130,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "Localytics",
    faviconUrl: "assets/images/logo/havas/HAVAS_isotype.svg",
    /* No loginImageUrl: localytics uses the CSS-rendered animated welcome panel
       (primary red background + two secondary-colored spheres) instead of a static image. */
};

const VOCENTO_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#00316C",
    accentColor: "#000000",
    tertiaryColor: "#03BFFE",
    primaryPalette: VOCENTO_PRIMARY,
    accentPalette: VOCENTO_SECONDARY,
    tertiaryPalette: VOCENTO_TERTIARY,
    primaryBaseShade: 500,
    accentBaseShade: 500,
    tertiaryBaseShade: 500,
    logoUrl: "assets/images/logo/vocento/Vocento_logotype.png",
    logoDarkUrl: "assets/images/logo/vocento/Vocento_logotype--white.png",
    logoMaxWidth: 180,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "Vocento",
    faviconUrl: "assets/images/logo/vocento/Vocento_isotype.png",
    /* No loginImageUrl: vocento uses the CSS-rendered animated welcome panel
       (primary navy background + two secondary/tertiary-colored spheres) instead of a static image. */
};

/** Sidn Primary (orange) - base 500 */
const SIDN_PRIMARY: ColorPalette = {
    50: "#FFFDFC",
    100: "#FDF1EE",
    200: "#F9D6CF",
    300: "#F5B7AB",
    400: "#F0927D",
    500: "#EC602B",
    600: "#D35626",
    700: "#B84B21",
    800: "#973D1B",
    900: "#6C2C13",
};

/** Sidn Secondary (dark navy) - base 500 */
const SIDN_SECONDARY: ColorPalette = {
    50: "#FCFCFD",
    100: "#EEEEF1",
    200: "#CECED6",
    300: "#A8A8B7",
    400: "#777792",
    500: "#070960",
    600: "#060856",
    700: "#05074B",
    800: "#04063D",
    900: "#03042C",
};

const SIDN_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#EC602B",
    accentColor: "#070960",
    primaryPalette: SIDN_PRIMARY,
    accentPalette: SIDN_SECONDARY,
    primaryBaseShade: 500,
    accentBaseShade: 500,
    logoUrl: "assets/images/logo/sidn/logo_sidn.svg",
    logoDarkUrl: "assets/images/logo/sidn/logo_sidn_white.svg",
    logoMaxWidth: 90,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "Sidn",
    faviconUrl: "assets/images/logo/sidn/isotype_sidn.svg",
    /* No loginImageUrl: sidn uses the CSS-rendered animated welcome panel
       (primary orange background + two navy-colored spheres) instead of a static image. */
};

/** Anima Primary (cyan) - base 500 */
const ANIMA_PRIMARY: ColorPalette = {
    50: "#F6FBFE",
    100: "#E5F3FD",
    200: "#C1E4FA",
    300: "#99D5F7",
    400: "#6AC7F4",
    500: "#00B8F1",
    600: "#16A2D7",
    700: "#1E8CBE",
    800: "#2276A6",
    900: "#22628E",
};

/** Anima Secondary (dark navy) - base 900 */
const ANIMA_SECONDARY: ColorPalette = {
    50: "#F1F2F5",
    100: "#E3E4EB",
    200: "#C7C9D6",
    300: "#ABAFC1",
    400: "#9095AD",
    500: "#767D9A",
    600: "#5C6586",
    700: "#424E74",
    800: "#273861",
    900: "#03244F",
};

const ANIMA_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#00B8F1",
    accentColor: "#03244F",
    primaryPalette: ANIMA_PRIMARY,
    accentPalette: ANIMA_SECONDARY,
    primaryBaseShade: 500,
    accentBaseShade: 900,
    logoUrl: "assets/images/logo/anima/Logo_navy_cyan_1.svg",
    logoDarkUrl: "assets/images/logo/anima/Anima_logotype--white.svg",
    logoMaxWidth: 130,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "Anima",
    faviconUrl: "assets/images/logo/anima/Anima_isotype.svg",
    /* No loginImageUrl: anima uses the CSS-rendered animated welcome panel
       (primary cyan background + two navy-colored spheres) instead of a static image. */
};

/** Clece Primary (purple) - base 500 */
const CLECE_PRIMARY: ColorPalette = {
    50: "#F4F0F7",
    100: "#E2DAEB",
    200: "#C6B4D7",
    300: "#A98FC3",
    400: "#8D69AF",
    500: "#70449B",
    600: "#5F3A84",
    700: "#4E306D",
    800: "#3E2555",
    900: "#2B1A3B",
};

/** Clece Secondary (black) - base 900 */
const CLECE_SECONDARY: ColorPalette = {
    50: "#F5F5F5",
    100: "#E0E0E0",
    200: "#BDBDBD",
    300: "#9E9E9E",
    400: "#757575",
    500: "#616161",
    600: "#424242",
    700: "#303030",
    800: "#1A1A1A",
    900: "#000000",
};

const CLECE_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#70449B",
    accentColor: "#000000",
    primaryPalette: CLECE_PRIMARY,
    accentPalette: CLECE_SECONDARY,
    primaryBaseShade: 500,
    accentBaseShade: 900,
    logoUrl: "assets/images/logo/cleece/Clece_logotype.svg",
    logoDarkUrl: "assets/images/logo/cleece/Clece_white_logotype.svg",
    logoMaxWidth: 130,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "Clece",
    faviconUrl: "assets/images/logo/cleece/clece_isotype.svg",
    loginImageUrl: "assets/images/logo/cleece/Clece_gradient.png",
};

/** Caetsu Two Primary (red) - base 500 */
const CAETSU_PRIMARY: ColorPalette = {
    50: "#FFE9EC",
    100: "#FFC7D0",
    200: "#FF93A3",
    300: "#FF5C75",
    400: "#FF2F51",
    500: "#EA0029",
    600: "#C20022",
    700: "#9B001B",
    800: "#730014",
    900: "#4D000D",
};

/** Caetsu Two Secondary (black) - base 900 */
const CAETSU_SECONDARY: ColorPalette = {
    50: "#F5F5F5",
    100: "#E0E0E0",
    200: "#BDBDBD",
    300: "#9E9E9E",
    400: "#757575",
    500: "#616161",
    600: "#424242",
    700: "#303030",
    800: "#1A1A1A",
    900: "#000000",
};

const CAETSU_BRANDING: Partial<OrganizationBranding> = {
    primaryColor: "#EA0029",
    accentColor: "#000000",
    primaryPalette: CAETSU_PRIMARY,
    accentPalette: CAETSU_SECONDARY,
    primaryBaseShade: 500,
    accentBaseShade: 900,
    logoUrl: "assets/images/logo/caetsu/logo_caetsu.svg",
    logoDarkUrl: "assets/images/logo/caetsu/CaetsuTwo_logotype--white.svg",
    logoMaxWidth: 168,
    logoHeight: 180,
    logoWidthPercent: 95,
    documentTitle: "Caetsu Two",
    faviconUrl: "assets/images/logo/caetsu/CaetsuTwo_isotype.svg",
    /* No loginImageUrl: caetsu-two uses the CSS-rendered animated welcome panel
       (primary red background + two secondary-colored spheres) instead of a static image. */
};

/**
 * Frontend-only branding by organization name (from user.organization.name in localStorage).
 * Keys are normalized names (lowercase, trimmed). No org IDs hardcoded.
 */
export const LOCAL_ORGANIZATION_BRANDING_BY_NAME: Record<string, Partial<OrganizationBranding>> = {
    zosmamedia: ZOSMA_BRANDING,
    "zosma media": ZOSMA_BRANDING,
    "geo-avante": GEO_AVANTE_BRANDING,
    "geo avante": GEO_AVANTE_BRANDING,
    geoavante: GEO_AVANTE_BRANDING,
    geohex: GEOHEX_BRANDING,
    "geo-hex": GEOHEX_BRANDING,
    "geo hex": GEOHEX_BRANDING,
    irismedia: IRISMEDIA_BRANDING,
    "iris media": IRISMEDIA_BRANDING,
    "iris-media": IRISMEDIA_BRANDING,
    irislens: IRISMEDIA_BRANDING,
    "iris lens": IRISMEDIA_BRANDING,
    "iris-lens": IRISMEDIA_BRANDING,
    global: GLOBAL_BRANDING,
    havas: HAVAS_BRANDING,
    "do it yourself": HAVAS_BRANDING,
    "do-it-yourself": HAVAS_BRANDING,
    doityourself: HAVAS_BRANDING,
    clearchannel: CLEAR_CHANNEL_BRANDING,
    "clear channel": CLEAR_CHANNEL_BRANDING,
    "clear-channel": CLEAR_CHANNEL_BRANDING,
    jcdecaux: JCDECAUX_BRANDING,
    "jc decaux": JCDECAUX_BRANDING,
    "jc-decaux": JCDECAUX_BRANDING,
    mediaplus: MEDIAPLUS_BRANDING,
    "media plus": MEDIAPLUS_BRANDING,
    "media-plus": MEDIAPLUS_BRANDING,
    "mediaplus-equlab": MEDIAPLUS_BRANDING,
    "mediaplus equlab": MEDIAPLUS_BRANDING,
    we24: WE24_BRANDING,
    "we 24": WE24_BRANDING,
    "we-24": WE24_BRANDING,
    loudintelligence: LOUD_INTELLIGENCE_BRANDING,
    "loud intelligence": LOUD_INTELLIGENCE_BRANDING,
    "loud-intelligence": LOUD_INTELLIGENCE_BRANDING,
    expertia: EXPERTIA_BRANDING,
    adissey: ADISSEY_BRANDING,
    imooh: IMOOH_BRANDING,
    localytics: LOCALYTICS_BRANDING,
    "havas portugal": LOCALYTICS_BRANDING,
    vocento: VOCENTO_BRANDING,
    sidn: SIDN_BRANDING,
    anima: ANIMA_BRANDING,
    "anima ads": ANIMA_BRANDING,
    animaads: ANIMA_BRANDING,
    clece: CLECE_BRANDING,
    "caetsu-two": CAETSU_BRANDING,
    "caetsu two": CAETSU_BRANDING,
};

/**
 * Subdomain -> org name key for branding (e.g. zosmatlas.geoactivate.ai -> Zosma branding).
 * Used when loading branding by subdomain before login.
 */
export const SUBDOMAIN_TO_ORG_NAME: Record<string, string> = {
    zosmatlas: "zosmamedia",
    "geo-avante": "geo-avante",
    geohex: "geohex",
    irislens: "irismedia",
    global: "global",
    doityourself: "havas",
    clearchannel: "clearchannel",
    jcdecaux: "jcdecaux",
    "mediaplus-equlab": "mediaplus",
    we24: "we24",
    loudintelligence: "loudintelligence",
    expertia: "expertia",
    adissey: "adissey",
    imooh: "imooh",
    localytics: "localytics",
    vocento: "vocento",
    sidn: "sidn",
    animaads: "anima",
    clece: "clece",
    "caetsu-two": "caetsu-two",
};

/** Get branding by subdomain (e.g. "zosmatlas" for zosmatlas.geoactivate.ai). */
export function getLocalBrandingForSubdomain(subdomain: string | null | undefined): Partial<OrganizationBranding> | null {
    if (!subdomain || !String(subdomain).trim()) return null;
    const key = String(subdomain).trim().toLowerCase();
    const orgName = SUBDOMAIN_TO_ORG_NAME[key];
    return orgName ? getLocalBrandingForOrganization(orgName) : null;
}

/** Organization names that always use the default theme (no override). */
export const DEFAULT_THEME_ORGANIZATIONS = ["appcelerate", "appcelerate-dev", "appcelerate-latam", "appcelerate latam", "globant"];

/** Get branding by organization name (e.g. user.organization.name). */
export function getLocalBrandingForOrganization(orgName: string | null | undefined): Partial<OrganizationBranding> | null {
    const key = normalizeOrgName(orgName);
    if (!key) return null;
    if (DEFAULT_THEME_ORGANIZATIONS.includes(key)) return null;
    return LOCAL_ORGANIZATION_BRANDING_BY_NAME[key] ?? null;
}

export function shouldUseDefaultTheme(orgName: string | null | undefined): boolean {
    const key = normalizeOrgName(orgName);
    if (!key) return true;
    return DEFAULT_THEME_ORGANIZATIONS.includes(key);
}
